import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { WithTooltip } from '@/components/ui/tooltip';
import { CountBadge, formatCount } from '@/components/ui/count-badge';
import { waitingLabel } from '@/lib/ui/waitingLabel';
import { APP_NAV_RAIL_TOOLTIP_OFFSET } from '@/lib/ui/appNavRail';
// The owner/superuser row filter, shared with `Sidebar` since T-20260916-4 —
// it needs the same answer to decide whether a group renders at all. One
// predicate, two callers; see that module's header.
import { isNavHrefActive, visibleNavItems } from './adminNavigation';

export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Story 24.5 — the solid glyph drawn while the row's route is active. */
  activeIcon?: React.ComponentType<{ className?: string }>;
  ownerOnly?: boolean;
  superuserOnly?: boolean; // Story PPA-2.1: Superuser role for prompt management
}

type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * Story 24.5 — the ONE sidebar row (DESIGN.md › Sidebar), used by the
 * top-level rows in `Sidebar.tsx` and the child rows below. Roles only.
 *
 * - 44px (`min-h-11`), `type-label` in `text-ink`, hover `bg-hover`, an INSET
 *   focus ring (`focus-ring-inset`) because the rows sit in a scroll container
 *   and a submenu panel that clips with `overflow-hidden`.
 * - Active: `bg-active-wash text-active-wash-ink`, the solid icon, a 3px
 *   leading bar in `bg-link`, and `aria-current="page"` — the bar and the
 *   icon fill mean colour never carries the state alone.
 * - `nested` (child rows): `px-2` and an `mr-2` icon gap instead of `px-3` /
 *   `mr-3`. The label budget in `Sidebar.test.tsx` is measured against both.
 * - `truncate` + `title` on the label: a future label that outgrows the
 *   measured budget ellipsises (and still says what it is) rather than
 *   wrapping inside a 44px row.
 * - `rail` (Story 24.6, the 64px icon rail at 1024–1279px): icon-only —
 *   centred, `px-0`, no icon margin, no label span — and wrapped in the
 *   shared Tooltip as its accessible name (`asLabel`, to the right). Order,
 *   `aria-current`, the bar and the wash are unchanged.
 * - `badgeCount` (Story 24.18 Tasks; Story 29.2 Chats): the shared
 *   `CountBadge`. Expanded, it sits inline at the end of the row (`ml-auto`,
 *   no ring); on the rail it sits at the icon's top-right with a 2px ring in
 *   the row's surface (`ring-canvas` at rest, `ring-active-wash` while
 *   active). While it shows, the row's name comes from `badgeLabel` —
 *   "Tasks, N waiting" (`waitingLabel`, the default) or "Chats, N unread"
 *   (`unreadLabel`) — the `aria-label` expanded, the Tooltip content on the
 *   rail. With no badge the name is the bare label.
 */
export function SidebarNavLink({
  name,
  href,
  icon: Icon,
  activeIcon: ActiveIcon,
  isActive,
  onClick,
  nested = false,
  rail = false,
  badgeCount,
  badgeLabel = waitingLabel,
}: {
  name: string;
  href: string;
  icon: IconComponent;
  activeIcon?: IconComponent;
  isActive: boolean;
  onClick?: () => void;
  nested?: boolean;
  /** Story 24.6 — icon-only, named by its Tooltip. */
  rail?: boolean;
  /** Story 24.18 — the badge count; no badge at 0 / undefined. */
  badgeCount?: number;
  /** Story 29.2 — names the row while the badge shows; defaults to `waitingLabel`. */
  badgeLabel?: (name: string, count: number | null | undefined) => string;
}) {
  const hasBadge = formatCount(badgeCount) !== null;
  const label = badgeLabel(name, badgeCount);
  const badgeTestId = `nav-badge-${href.replace(/^\//, '').replace(/\//g, '-')}`;
  const Glyph = isActive && ActiveIcon ? ActiveIcon : Icon;
  const padding = rail ? 'justify-center px-0' : nested ? 'px-2' : 'px-3';
  const iconClass = rail ? 'size-5 shrink-0' : `size-5 ${nested ? 'mr-2' : 'mr-3'} shrink-0`;
  const link = (
    <Link
      to={href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      // Rail: the Tooltip (`asLabel`) supplies the name through Slot — so the
      // prop must be ABSENT there, never `undefined`, or it clobbers the
      // merged one. Expanded: only while a badge shows, so a badge-less row
      // keeps its text as its name.
      {...(!rail && hasBadge ? { 'aria-label': label } : {})}
      className={`relative flex items-center min-h-11 ${padding} rounded-md type-label focus-ring-inset ${
        isActive ? 'bg-active-wash text-active-wash-ink' : 'text-ink hover:bg-hover'
      }`}
    >
      {isActive && (
        <span
          aria-hidden="true"
          data-testid="nav-active-bar"
          className="absolute left-0 inset-y-2 w-[3px] rounded-full bg-link"
        />
      )}
      {rail && hasBadge ? (
        <span className="relative inline-flex shrink-0">
          <Glyph className={iconClass} aria-hidden="true" />
          <CountBadge
            count={badgeCount}
            data-testid={badgeTestId}
            ringClassName={`ring-2 ${isActive ? 'ring-active-wash' : 'ring-canvas'}`}
            className="absolute -right-3 -top-2"
          />
        </span>
      ) : (
        <Glyph className={iconClass} aria-hidden="true" />
      )}
      {!rail && (
        <span className="truncate" title={name}>
          {name}
        </span>
      )}
      {!rail && hasBadge && (
        <CountBadge count={badgeCount} data-testid={badgeTestId} className="ml-auto" />
      )}
    </Link>
  );
  return rail ? (
    <WithTooltip asLabel side="right" sideOffset={APP_NAV_RAIL_TOOLTIP_OFFSET} content={label}>
      {link}
    </WithTooltip>
  ) : (
    link
  );
}

interface SidebarSubmenuProps {
  title: string;
  icon: IconComponent;
  /** Shown on the toggle while one of the submenu's routes is active. */
  activeIcon?: IconComponent;
  items: NavItem[];
  isOpen: boolean;
  onToggle: () => void;
  onItemClick?: () => void;
  isOwner?: boolean;
  isSuperuser?: boolean; // Story PPA-2.1: Superuser flag for conditional nav display
  /** Story 24.6 — the icon rail: icon-only toggle and child rows, no indent. */
  rail?: boolean;
}

export default function SidebarSubmenu({
  title,
  icon: Icon,
  activeIcon: ActiveIcon,
  items,
  isOpen,
  onToggle,
  onItemClick,
  isOwner = false,
  isSuperuser = false,
  rail = false,
}: SidebarSubmenuProps) {
  const location = useLocation();

  // Is any child route active? Drives the toggle's solid icon only — the
  // toggle itself never takes the active fill (the child row carries it).
  const isSubmenuActive = items.some((item) => isNavHrefActive(location.pathname, item.href));

  // Filter items based on owner and superuser status.
  // Story PPA-2.1: conditional rendering (not CSS hiding) to prevent DOM inspection.
  const visibleItems = visibleNavItems(items, { isOwner, isSuperuser });

  // T-20260916-2 — nothing visible, so no header: a toggle over an empty panel
  // advertises a section that leads nowhere. Since Story 24.5 the only
  // submenu is Engineering, `superuserOnly` throughout, so this early return
  // is what keeps it out of a non-superuser's DOM (Sidebar also pre-filters).
  //
  // Placed AFTER `isSubmenuActive` deliberately: hooks above must run on every
  // render (rules of hooks).
  if (visibleItems.length === 0) {
    return null;
  }

  // T-20260916-3 review P13 — stable handles for the panel and its toggle.
  const panelId = `submenu-panel-${title}`;
  const ToggleIcon = isSubmenuActive && ActiveIcon ? ActiveIcon : Icon;

  return (
    <div data-testid={`submenu-${title}`}>
      {/* The toggle has the row's shape (44px, `type-label`, inset ring) so
          the group reads as one more row of the column. `aria-expanded` and
          `aria-controls` make it a real disclosure. */}
      {rail ? (
        /* Story 24.6 — icon-only on the rail: the Tooltip is the name, no
           chevron (there is no room; `aria-expanded` carries the state). */
        <WithTooltip asLabel side="right" sideOffset={APP_NAV_RAIL_TOOLTIP_OFFSET} content={title}>
          <button
            type="button"
            onClick={onToggle}
            data-testid={`submenu-toggle-${title}`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            className="w-full flex items-center justify-center min-h-11 px-0 rounded-md type-label text-ink hover:bg-hover focus-ring-inset"
          >
            <ToggleIcon className="size-5 shrink-0" aria-hidden="true" />
          </button>
        </WithTooltip>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          data-testid={`submenu-toggle-${title}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="w-full flex items-center min-h-11 px-3 rounded-md type-label text-ink hover:bg-hover focus-ring-inset"
        >
          <ToggleIcon className="size-5 mr-3 shrink-0" aria-hidden="true" />
          <span className="flex-1 text-left truncate" title={title}>
            {title}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className={`size-4 shrink-0 text-ink-muted transition-transform duration-200 motion-reduce:transition-none ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      )}

      {/* `max-h-[32rem]` (512px) holds about ten 48px rows; Engineering has
          four (~190px with gaps), so nothing clips. `overflow-hidden` drives
          the collapse transition from `max-h-0` — and is why every row in
          here draws an INSET focus ring. Raise the cap before a submenu grows
          past ten rows. */}
      <div
        id={panelId}
        data-testid={`submenu-panel-${title}`}
        // Closed: the rows stay in the DOM (the visibility suites rely on
        // that) but `inert` keeps keyboard focus off four invisible links.
        inert={!isOpen}
        className={`overflow-hidden transition-all duration-200 ease-in-out motion-reduce:transition-none ${
          isOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {/* T-20260916-5 — `ml-4` (16px) with the child rows' `px-2` / `mr-2`.
            These are the rows the rail width is sized against: the measured
            label budget (scrollbar allowed for) lives in `Sidebar.test.tsx`
            and the arithmetic on the `<aside>` in `Sidebar.tsx`.
            Story 24.6 — on the rail there is no room for an indent, so the
            panel draws a hairline above its rows instead and the group still
            reads as a group. */}
        <div className={rail ? 'mt-1 pt-1 border-t border-line space-y-1' : 'ml-4 mt-1 space-y-1'}>
          {visibleItems.map((item) => (
            <SidebarNavLink
              key={item.name}
              name={item.name}
              href={item.href}
              icon={item.icon}
              activeIcon={item.activeIcon}
              isActive={isNavHrefActive(location.pathname, item.href)}
              onClick={onItemClick}
              nested
              rail={rail}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
